import {
	type AssetClassesModel,
	type AssetClassSummary,
	type LabelValueV2,
	type MacroContextDriverDTO,
	type MarketOutlookTimeSeriesDTO,
	MarketAssetClassesControllerApiFactory,
	OutlookControllerV3ApiFactory,
} from "@eu.mdotm.api/sphere";
import {
	CircularProgressBar,
	ClickableArea,
	LocalOverlay,
	ProgressBar,
	ScrollWrapper,
	Svg,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { useDebouncedMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { mapBetweenRanges } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";
import { useFunctionalAreas } from "$root/App/context";
import { getApiGen } from "$root/api/factory";
import { useApiGen } from "$root/api/hooks";
import { BubbleChartSvg, SmartBubbleFragment } from "$root/components/BubbleChart/BubbleChartSvg";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { DonutChartSvg } from "$root/components/DonutChart/DonutChartSvg";
import HierarchicalMultiSelect, {
	type OptionTreeNode,
	withPlaceholder,
} from "$root/components/HierarchicalMultiSelect";
import { IconWalls } from "$root/components/IconWall";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { OutlookWidgetData } from "$root/components/OutlookWidgetData";
import { PageDownloadMenu } from "$root/components/PageDownloadMenu";
import { PageHeader } from "$root/components/PageHeader";
import ReactQueryWrapper, { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { Option } from "$root/components/shared";
import { feelsConverter } from "$root/functional-areas/market-view/analysis/feels-converter";
import { SentimentBadge } from "$root/functional-areas/market-view/analysis/SentimentBadge";
import type { SentimentType } from "$root/functional-areas/market-view/analysis/sentiment";
import PositioningBadge, {
	positioningPresets,
	positioningValueTypeMap,
} from "$root/functional-areas/market-view/PositioningBadge";
import { outlookRepository, type SectorVariants } from "$root/functional-areas/outlook/repository";
import TimeSeriesModal from "$root/functional-areas/outlook/TimeSeriesModal";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import useTypeToLabel from "$root/hooks/useTypeToLabel";
import { useLocaleFormatters } from "$root/localization/hooks";
import { useMappedQuery, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import {
	builtInCaseInsensitiveSortFor,
	colorGenerator,
	customObjectEntriesFn,
	customObjectValuesFn,
	downloadContentDisposition,
	getQueryParam,
	getThemeCssVars,
	lazy,
	maxArrayLike,
	qualifier,
	replaceSpaceWith_,
} from "$root/utils";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { Card } from "$root/widgets-architecture/layout/Card";
import WidgetsGrid from "$root/widgets-architecture/layout/WidgetsGrid";
import { CommentaryMDBlockContent } from "$root/widgets-architecture/widgets/CommentaryMDBlock";
import { ScatterPlotLimits } from "./data";

const PAGE_NAME = "ASSET_CLASS";

const DEFAULT_FILTER = "EQ";
const DEFAULT_AREA = "EU";
const DEFAULT_SECTOR = "ALL_SECTORS";

type MultiFilterSelection = {
	assetClass: string | undefined;
	area: string | undefined;
	sector: string | undefined;
};

/**
 * @internal — exported for tests
 *
 * Resolves the filter selection to use for the page from the URL query string, validated against the
 * available option tree. If the URL names a combination that does not exist in the tree (unknown
 * asset class, or an area/sector missing under it while that level has options), it falls back to the
 * first available leaf. A valid combination is returned as-is.
 */
export function computeMultiFilterFromSearch(
	optionTreeNode: OptionTreeNode,
	searchString: string,
): MultiFilterSelection {
	const candidateSelection = {
		assetClass: getQueryParam(searchString, "filter") || DEFAULT_FILTER,
		area: getQueryParam(searchString, "area") || DEFAULT_AREA,
		sector: getQueryParam(searchString, "sector") || DEFAULT_SECTOR,
	};

	const assetClassOption = optionTreeNode.find((opt) => opt.value === candidateSelection.assetClass);
	let areaOption:
		| undefined
		| (Option<string | undefined> & {
				subOptions?: OptionTreeNode;
		  });
	let sectorOption:
		| undefined
		| (Option<string | undefined> & {
				subOptions?: OptionTreeNode;
		  });
	if (assetClassOption) {
		areaOption = assetClassOption.subOptions?.find((opt) => opt.value === candidateSelection.area);
		if (areaOption) {
			sectorOption = areaOption.subOptions?.find((opt) => opt.value === candidateSelection.sector);
		}
	}
	if (
		!assetClassOption ||
		(!areaOption && assetClassOption.subOptions && assetClassOption.subOptions.length > 0) ||
		(!sectorOption && areaOption?.subOptions && areaOption?.subOptions.length > 0)
	) {
		return {
			assetClass: optionTreeNode[0].value,
			area: optionTreeNode[0].subOptions?.[0]?.value,
			sector: optionTreeNode[0].subOptions?.[0]?.subOptions?.[0]?.value,
		};
	}
	return candidateSelection;
}

/**
 * @internal — exported for tests
 *
 * Builds the hierarchical asset-class -> geography/type -> sector option tree the page filters by,
 * from the raw universe asset-class list. Groups the assets by type, normalises each area value to an
 * uppercase underscore code, injects an ALL_SECTORS entry ahead of the equity sectors, derives the
 * fixed-income geographies from the first three characters of each label (deduplicated), and sorts
 * every level case-insensitively by label.
 */
export function buildAssetClassOptionTree(
	allAssets: Array<AssetClassesModel>,
	t: TFunction,
	getLabel: (type: string) => string,
): OptionTreeNode {
	const sortByLabel = builtInCaseInsensitiveSortFor("label");

	const assetTypeMap = {
		Equity: "equity",
		"Fixed income": "fixedIncome",
		Commodities: "commodities",
	} as const;

	const {
		equity = [],
		fixedIncome = [],
		commodities = [],
	} = groupBy(allAssets, (asset) => assetTypeMap[asset.type as keyof typeof assetTypeMap]);

	const equityAreaOptions: Option<string>[] = equity
		.map((el) => ({
			value: replaceSpaceWith_(el.label!).toUpperCase(),
			label: getLabel(`EQ_${el.label}`),
		}))
		.sort(sortByLabel);

	const equitySectorOptionsByArea = equity.reduce(
		(byArea, area) => {
			byArea[area.label!] = !area?.sector
				? []
				: [
						{
							value: "ALL_SECTORS",
							label: t("ASSET_CLASS_LABEL.ALL_SECTORS"),
						},
						...(area.sector ?? [])
							.map((el) => ({
								value: el.replace(" ", "_").toUpperCase(),
								label: t(
									// eslint-disable-next-line @typescript-eslint/ban-ts-comment
									`EQ_SECTOR.${el.replace(" ", "_").toUpperCase()}`,
									// eslint-disable-next-line @typescript-eslint/ban-ts-comment
									// @ts-expect-error
									t(`EQ_SECTOR.${el.replace(" ", "").toUpperCase()}`),
								),
							}))
							.sort(sortByLabel),
					];
			return byArea;
		},
		{} as Record<string, Option<string>[]>,
	);

	const commoditiesAreaOptions: Option<string>[] = commodities
		.map((el) => ({
			value: replaceSpaceWith_(el.label!).toUpperCase(),
			label: el.label!,
		}))
		.sort(sortByLabel);

	const commoditiesSectorOptionsByArea = commodities.reduce(
		(byArea, area) => {
			byArea[area.label!] = !area?.sector
				? []
				: [
						{
							value: "ALL_SECTORS",
							label: t("ASSET_CLASS_LABEL.ALL_SECTORS"),
						},
					];
			return byArea;
		},
		{} as Record<string, Option<string>[]>,
	);

	// temporary map assuming label are mapped like this
	const fixedIncomeGeo: Record<string, string> = t("FI_GEO", {
		returnObjects: true,
	});
	const fixedIncomeAreaOptions: Option<string>[] = Array.from(
		new Set(fixedIncome.map((el) => el.label!.toString().slice(0, 3).replace(" ", "").toUpperCase())),
	)
		.map((el) => ({
			value: el,
			label: fixedIncomeGeo[el] ?? el,
		}))
		.sort(sortByLabel);
	// temporary map assuming label are mapped like this
	// const fixedIncomeSectors: Record<string, string> = t("FI_BOND", { returnObjects: true });
	const fixedIncomeSectorOptionsByArea = fixedIncome.reduce(
		(byArea, areaAndSector) => {
			const area = areaAndSector.label!.toString().slice(0, 3).replace(" ", "").toUpperCase();
			byArea[area] = byArea[area] || [];
			byArea[area].push({
				value: replaceSpaceWith_(areaAndSector.label!.replace(`${area} `, "")).toUpperCase(),
				label: areaAndSector.label!.replace(`${area} `, ""),
			});
			return byArea;
		},
		{} as Record<string, Option<string>[]>,
	);
	for (const k in fixedIncomeSectorOptionsByArea) {
		fixedIncomeSectorOptionsByArea[k].sort(sortByLabel);
	}

	const result: OptionTreeNode = withPlaceholder(
		t("OUTLOOK.SELECT_ASSET_CLASS"),
		[
			{
				value: "EQ",
				label: t("EQUITY"),
				subOptions: withPlaceholder(
					t("OUTLOOK.SELECT_GEOGRAPHY"),
					equityAreaOptions.map((areaOption) => ({
						...areaOption,
						subOptions: equitySectorOptionsByArea[areaOption.value],
					})),
				),
			},
			{
				value: "FI",
				label: t("OUTLOOK.FI"),
				subOptions: withPlaceholder(
					t("OUTLOOK.SELECT_GEOGRAPHY"),
					fixedIncomeAreaOptions.map((areaOption) => ({
						...areaOption,
						subOptions: fixedIncomeSectorOptionsByArea[areaOption.value],
					})),
				),
			},
			{
				value: "CO",
				label: t("OUTLOOK.CO"),
				subOptions: withPlaceholder(
					t("OUTLOOK.SELECT_TYPE"),
					commoditiesAreaOptions.map((areaOption) => ({
						...areaOption,
						subOptions: commoditiesSectorOptionsByArea[areaOption.value],
					})),
				),
			},
		].sort(sortByLabel),
	);
	return result;
}

const AssetClass = (): React.JSX.Element => {
	const history = useHistory();
	const crumbs = useCrumbs();
	const location = useLocation();
	const { t } = useTranslation();
	const { getLabel } = useTypeToLabel();

	const [multiFilter, setMultiFilter] = useState<{
		assetClass: string | undefined;
		area: string | undefined;
		sector: string | undefined;
	} | null>(null);

	const updateFilterUsingSearchString = useCallback((optionTreeNode: OptionTreeNode, searchString: string) => {
		setMultiFilter(computeMultiFilterFromSearch(optionTreeNode, searchString));
	}, []);

	const universeApi = useApiGen(MarketAssetClassesControllerApiFactory);

	const optionTree = useQueryNoRefetch(["assetClassOptionsOld"], {
		queryFn: async () => {
			const { data: allAssets } = await universeApi.getMarketAssetClasses();
			return buildAssetClassOptionTree(allAssets, t, getLabel);
		},
	});
	useQueryReactor(optionTree, {
		onSuccess(data) {
			updateFilterUsingSearchString(data, location.search);
		},
	});

	// The following code manages the "authoritativeness" of the URL.
	// The problem we are trying to solve is that the URL may contain
	// an invalid combination of filters, therefore it cannot be used
	// directly in the page. That's why we have a custom multiFilter state
	// which always contains a valid combination. Moreover, when the state changes
	// the URL is updated accordingly, while when the URL changes (e.g. because the
	// user clicked some link), the state should be updated only after validating the
	// parameters contained in the query string.
	const historyRef = useUnsafeUpdatedRef(history);
	const locationRef = useUnsafeUpdatedRef(location);
	const ignoreNextHistoryUpdateRef = useRef(false);

	const optionTreeDataRef = useUnsafeUpdatedRef<OptionTreeNode | null>(optionTree.data ?? null);

	useEffect(() => {
		if (ignoreNextHistoryUpdateRef.current) {
			ignoreNextHistoryUpdateRef.current = false;
			return;
		}
		if (optionTreeDataRef.current) {
			updateFilterUsingSearchString(optionTreeDataRef.current, location.search);
		}
	}, [location.search, optionTreeDataRef, updateFilterUsingSearchString]);

	useEffect(() => {
		if (multiFilter) {
			const href = `?filter=${encodeURIComponent(multiFilter.assetClass ?? "")}&area=${encodeURIComponent(
				multiFilter.area ?? "",
			)}${multiFilter.sector ? `&sector=${encodeURIComponent(multiFilter.sector)}` : ""}`;

			if (locationRef.current.search !== href) {
				ignoreNextHistoryUpdateRef.current = true;
				historyRef.current.replace(href);
			}
		}
	}, [historyRef, locationRef, multiFilter]);

	useFunctionalAreas(
		() => ({
			areas: "outlookFocus",
			data: {
				firstFilter: multiFilter?.assetClass,
				secondFilter: multiFilter?.area,
				thirdFilter: multiFilter?.sector,
			},
		}),
		[multiFilter?.assetClass, multiFilter?.area, multiFilter?.sector],
	);

	return (
		<>
			<PageHeader
				crumbs={crumbs.marketInsights("Outlook focus")}
				subTitle={<OutlookWidgetData />}
				titleAction={
					<PageDownloadMenu
						area="outlook-focus"
						actions={[
							{
								icon: "xls",
								label: "Market outlook",
								onClickAsync: async () =>
									downloadContentDisposition(
										await getApiGen(OutlookControllerV3ApiFactory).createExcelReport({
											responseType: "blob",
										}),
									),
								"data-qualifier": "OutlookFocus/DropdownMenu/DropdownItem(XLSReport)",
							},
						]}
					/>
				}
			/>
			{optionTree.isError ? (
				t("SOMETHING_WENT_WRONG")
			) : optionTree.isLoading ? (
				<ProgressBar value="indeterminate" />
			) : (
				optionTree.data &&
				multiFilter && (
					<HierarchicalMultiSelect
						selection={[multiFilter.assetClass, multiFilter.area, multiFilter.sector]}
						optionTree={optionTree.data}
						label={t("OUTLOOK.LOOKING_AT")}
						onChange={([newAssetClass, newArea, newSector]) => {
							setMultiFilter({
								assetClass: newAssetClass,
								area: newArea,
								sector: newSector,
							});
						}}
					/>
				)
			)}
			{multiFilter && optionTree.isFetched && (
				<>
					<MacroDynamicsSection multiFilter={multiFilter} />
					<UniverseFiltersContext.Provider
						value={{
							firstFilter: multiFilter.assetClass ?? "",
							secondFilter: multiFilter.area ?? "",
							thirdFilter: multiFilter.sector ?? "",
						}}
					>
						<WidgetsGrid gridName={PAGE_NAME} />
					</UniverseFiltersContext.Provider>
				</>
			)}
		</>
	);
};

export default AssetClass;

type PerformanceIndicatorsProps = Required<MacroContextDriverDTO> & {
	color: string;
};

const sentimentList = ["negative", "neutral", "positive"] satisfies SentimentType[];

/**
 * @internal — exported for tests
 *
 * Maps the raw macro-context drivers into the shape the card renders. It drops any driver that carries
 * a null/undefined field, converts the "Quantitative Market Sentiment" driver's numeric current into a
 * sentiment word (and clears its level/trend), normalises a vs-peers rank whose integer part is 0 up to
 * 1, colours each surviving driver by its position, and returns them sorted by descending relevance.
 */
export function mapMacroDrivers(
	macroDrivers: Array<MacroContextDriverDTO>,
): Array<Omit<PerformanceIndicatorsProps, "current"> & { current: string | number }> {
	function validateValue<T>(data: T): boolean | T {
		return data !== null && data !== undefined ? data : false;
	}

	return macroDrivers
		.flatMap((x, i) => {
			const isInvalid = customObjectValuesFn(x).some((macroContext) => validateValue(macroContext) === false);

			if (isInvalid) {
				return [];
			}

			return [
				{
					...x,
					...(x.label === "Quantitative Market Sentiment" ? { level: undefined, trend: undefined } : {}),
					current:
						x.label === "Quantitative Market Sentiment"
							? sentimentList[Math.floor(Number(x.current) - 1)]
							: Number(x.current),
					color: colorGenerator(i, colors()),
					returnVsPeers: Math.floor(x.returnVsPeers ?? 0) === 0 ? 1 : x.returnVsPeers,
					volatilityVsPeers: Math.floor(x.volatilityVsPeers ?? 0) === 0 ? 1 : x.volatilityVsPeers,
					diversificationVsPeers: Math.floor(x.diversificationVsPeers ?? 0) === 0 ? 1 : x.diversificationVsPeers,
				} as Omit<PerformanceIndicatorsProps, "current"> & {
					current: string | number;
				},
			];
		})
		.sort((a, b) => (a.relevance > b.relevance ? -1 : 1));
}

function MacroDynamicsSection({
	multiFilter,
}: {
	multiFilter: { assetClass?: string; area?: string; sector?: string };
}) {
	const { assetClass, geography, sector } = useOutlookComposition(
		multiFilter?.assetClass ?? "",
		multiFilter?.area ?? "",
		multiFilter?.sector ?? "",
	);

	const macroContextQueryRaw = outlookRepository.macroContextDriver.useQuery({
		assetClass,
		geography,
		sector,
	});
	const macroContextQuery = useMappedQuery(mapMacroDrivers, [sentimentList], macroContextQueryRaw);

	return (
		<div>
			<ReactQueryWrapper
				{...outlookRepository.acMetricsStandard.getQueryConfig({
					assetClass,
					geography,
					sector,
				})}
				keepPreviousData
			>
				{(data, rq) => (
					<div className="relative z-0">
						<div className="mb-3">
							<ReactQueryWrapper
								{...outlookRepository.varByAssetClass.getQueryConfig({
									assetClass,
									geography,
									sector,
								})}
								keepPreviousData
							>
								{(VaR, subRq) => (
									<div className="relative z-0">
										<LocalOverlay
											show={subRq.isFetching}
											className="z-10"
											style={{
												background: "white",
											}}
										>
											<CircularProgressBar value="indeterminate" />
										</LocalOverlay>
										<SummaryCard summary={data} VaR={VaR} />
									</div>
								)}
							</ReactQueryWrapper>
						</div>
						<div className="grid grid-cols-3 mb-3 gap-3">
							<CurrentPositioningCard
								summary={data}
								assetClass={assetClass}
								geography={geography}
								sector={sector}
								isSummaryLoading={rq.isFetching}
							/>
							<Card className="col-span-2 h-[240px]" title="Comment">
								<CommentaryMDBlockContent
									fixedCommentary
									firstFilter={multiFilter.assetClass ?? ""}
									secondFilter={multiFilter.area ?? ""}
									thirdFilter={multiFilter.sector ?? ""}
								/>
							</Card>
						</div>
					</div>
				)}
			</ReactQueryWrapper>
			<ReactQueryWrapperBase query={macroContextQuery}>
				{(data, subRq) =>
					!data.length ? (
						<div className="my-4 h-72">
							<IconWalls.DataNotAvailable />
						</div>
					) : (
						<InteractiveMacroDriverCard
							performanceIndicators={data ?? []}
							assetClass={assetClass}
							sector={sector}
							isFetching={subRq.isFetching}
						/>
					)
				}
			</ReactQueryWrapperBase>
		</div>
	);
}

/** @internal — exported for tests */
export function plotKeyProvider(
	assetClass: "EQUITY" | "COMMODITIES" | "FIXED_INCOME" | "FIXEDINCOME",
	sector: SectorVariants,
): keyof typeof ScatterPlotLimits {
	if (assetClass !== "EQUITY") {
		return assetClass;
	}

	if (sector !== "ALL") {
		return "EQUITYGEO";
	}

	return "EQUITYSECTORS";
}

/** @internal — exported for tests */
export function ordinal(n: number): string {
	const stringified = n.toFixed(0);
	const lastDigit = stringified.slice(-1);
	if (lastDigit === "1") {
		return `${stringified}st`;
	}
	if (lastDigit === "2") {
		return `${stringified}nd`;
	}
	if (lastDigit === "3") {
		return `${stringified}rd`;
	}
	return `${stringified}th`;
}

function InteractiveMacroDriverCard({
	performanceIndicators,
	assetClass,
	sector,
	isFetching,
}: {
	performanceIndicators: Array<Omit<PerformanceIndicatorsProps, "current"> & { current: string | number }>;
	assetClass: "EQUITY" | "COMMODITIES" | "FIXED_INCOME" | "FIXEDINCOME";
	sector: SectorVariants;
	isFetching: boolean;
}) {
	const [selectedIndicatorIndex, setSelectedIndicatorIndex] = useState<number | null>(null);
	const [_hoveringIndicatorIndex, setHoveringIndicatorIndex] = useState<number | null>(null);
	const { value: hoveringIndicatorIndex } = useDebouncedMemo(() => _hoveringIndicatorIndex, [_hoveringIndicatorIndex], {
		debounceInterval: 30,
	});
	const maxRelevance = maxArrayLike(performanceIndicators, (indicator) => indicator.relevance ?? 0);

	const plotKey = useMemo(() => plotKeyProvider(assetClass, sector), [assetClass, sector]);
	const { returnsLimits, volatilityLimits } = useMemo(() => {
		const acScatterPlotLimits = ScatterPlotLimits[plotKey];
		return {
			returnsLimits: acScatterPlotLimits.RETURNS,
			volatilityLimits: acScatterPlotLimits.VOLATILITY,
		};
	}, [plotKey]);

	const { formatNumber } = useLocaleFormatters();

	const performanceTodayScrollableAreaRef = useRef<HTMLDivElement | null>(null);
	const performanceCardContainerRef = useRef<HTMLDivElement | null>(null);

	const levelsLabel = {
		1: "Low",
		2: "Mid",
		3: "High",
	};

	const trendsLabel = {
		1: "Down",
		2: "Lateral",
		3: "Up",
	};

	function macroDynamicsColorGenerator(
		value: number,
		range: {
			min: number;
			max: number;
			intermediateMin: number;
			intermediateMax: number;
			invert?: boolean;
		},
	) {
		const ranges = {
			strong_underweight: range.invert ? { min: range.max, max: Infinity } : { min: -Infinity, max: range.min },
			underweight: range.invert
				? { min: range.intermediateMax, max: range.max }
				: { min: range.min, max: range.intermediateMin },
			neutral: range.invert
				? { min: range.intermediateMin, max: range.intermediateMax }
				: { min: range.intermediateMin, max: range.intermediateMax },
			overweight: range.invert
				? { min: range.min, max: range.intermediateMin }
				: { min: range.intermediateMax, max: range.max },
			strong_overweight: range.invert ? { min: -Infinity, max: range.min } : { min: range.max, max: Infinity },
		};

		const colorsMap = {
			strong_underweight: themeCSSVars.palette_R500,
			underweight: themeCSSVars.palette_R300,
			neutral: themeCSSVars.palette_S300,
			overweight: themeCSSVars.palette_P300,
			strong_overweight: themeCSSVars.palette_P500,
		} satisfies Record<keyof typeof ranges, string>;

		const currentRange = customObjectEntriesFn(ranges)
			.reverse()
			.find(([_key, r]) => r.min <= value && r.max >= value)![0];

		return colorsMap[currentRange];
	}

	const ranges = {
		strong_underweight: { min: 0, max: 10 },
		underweight: { min: 10, max: 30 },
		neutral: { min: 30, max: 70 },
		overweight: { min: 70, max: 90 },
		strong_overweight: { min: 90, max: 100 },
	} as const;

	const threshold = [
		ranges.strong_underweight.min,
		ranges.strong_underweight.max,
		ranges.underweight.max,
		ranges.neutral.max,
		ranges.overweight.max,
		ranges.strong_overweight.max,
	] as const;

	function crossSectionalColorGenerator(value: number) {
		if (value >= ranges.strong_underweight.min && value < ranges.strong_underweight.max) {
			return themeCSSVars.palette_P500;
		}

		if (value >= ranges.underweight.min && value < ranges.underweight.max) {
			return themeCSSVars.palette_P300;
		}

		if (value >= ranges.neutral.min && value < ranges.neutral.max) {
			return themeCSSVars.palette_S300;
		}

		if (value >= ranges.overweight.min && value < ranges.overweight.max) {
			return themeCSSVars.palette_R300;
		}

		return themeCSSVars.palette_R500;
	}

	function syncScrollableAreaByIndex(index: number) {
		setSelectedIndicatorIndex(selectedIndicatorIndex === index ? null : index);
		const cardEl = performanceCardContainerRef.current?.querySelector(`[data-index="${index}"]`) as
			| HTMLElement
			| undefined;
		if (cardEl && performanceTodayScrollableAreaRef.current) {
			performanceTodayScrollableAreaRef.current.scrollLeft =
				cardEl.offsetLeft + cardEl.offsetWidth / 2 - performanceTodayScrollableAreaRef.current.clientWidth / 2;
		}
	}

	return (
		<Card title="Driving macro context" className="mb-3">
			<div className="@container relative z-0" data-qualifier={qualifier.outlook.focus.macroDynamics}>
				<LocalOverlay
					show={isFetching}
					className="z-10"
					style={{
						background: "white",
					}}
				>
					<CircularProgressBar value="indeterminate" />
				</LocalOverlay>
				<div className={`-mx-4 px-4 py-2 bg-[var(--palette-N20)] border-t border-b border-[color:var(--palette-N100)]`}>
					<Text as="div" type="Body/L/Bold">
						Relevant variables
					</Text>

					<ScrollWrapper direction="horizontal" innerRef={performanceTodayScrollableAreaRef}>
						<div className="flex gap-2 py-3" ref={performanceCardContainerRef}>
							<ForEach collection={performanceIndicators}>
								{(item, index) => {
									const htmlWidth = 150;
									const viewBoxWidth = 150;
									const diameter = mapBetweenRanges(item?.relevance, 0, maxRelevance, 0, 120);
									return (
										<div data-index={index} /* className={`border border-[var(--palette-N50)]`} */ className="relative">
											<ClickableArea
												onMouseEnter={() => setHoveringIndicatorIndex(index)}
												onMouseLeave={() => setHoveringIndicatorIndex(null)}
												onClick={() => setSelectedIndicatorIndex(selectedIndicatorIndex === index ? null : index)}
												wrapperAppearance={{
													className: {
														[`relative z-0 min-w-[300px] bg-white rounded-md p-2 border transition-transform`]: true,
														"shadow-md": !(hoveringIndicatorIndex === index || selectedIndicatorIndex === index),
														"-translate-y-1 shadow-lg":
															hoveringIndicatorIndex === index || selectedIndicatorIndex === index,

														[`border-[var(--palette-N50)]`]: selectedIndicatorIndex !== index,
														[`border-[var(--palette-N200)]`]: selectedIndicatorIndex === index,
													},
												}}
											>
												<div className="absolute z-20">
													<div className="flex items-center gap-2">
														<ColoredRectangle variant="vertical" color={item.color} />
														<Text type="Body/L/Bold">{item.label}</Text>
													</div>
												</div>
												<div className="flex items-center gap-2">
													<div className="flex-1 min-w-0 flex flex-col gap-4">
														<div>
															<div>
																<Text type="Body/S/Book">Current value</Text>
															</div>
															<div>
																{typeof item.current === "number" ? (
																	<Text type="Body/M/Bold">{`${formatNumber(item.current)}%`}</Text>
																) : (
																	<SentimentBadge
																		sentiment={
																			item.current as Exclude<SentimentType, "super-positive" | "super-negative">
																		}
																		indicator="driver"
																	>
																		{item.current}
																	</SentimentBadge>
																)}
															</div>
														</div>
														{item.level && item.trend && (
															<div>
																<div>
																	<Text type="Body/S/Book">level - trend</Text>
																</div>
																<div>
																	<Text type="Body/M/Bold" className="uppercase">
																		{levelsLabel[item.level as unknown as keyof typeof levelsLabel]}-
																		{trendsLabel[item.trend as unknown as keyof typeof trendsLabel]}
																	</Text>
																</div>
															</div>
														)}
													</div>
													<div className="relative z-0">
														<div className="absolute z-10 inset-x-0 flex justify-center top-12">
															<Text type="Body/S/Book" color={themeCSSVars.palette_N500}>
																Relevance
															</Text>
														</div>
														<Svg
															className="relative z-0"
															style={{
																width: htmlWidth,
																height: htmlWidth,
															}}
															viewBox={{
																width: viewBoxWidth,
																height: viewBoxWidth,
															}}
														>
															<SmartBubbleFragment
																centerX={viewBoxWidth / 2}
																centerY={viewBoxWidth / 2}
																color={themeCSSVars.palette_N100}
																diameter={diameter}
																text={`${formatNumber(item.relevance, 0)}%`}
																textColor={themeCSSVars.palette_N700}
																preferredOverflowPosition="bottom"
															/>
														</Svg>
													</div>
												</div>
											</ClickableArea>
										</div>
									);
								}}
							</ForEach>
						</div>
					</ScrollWrapper>
				</div>
				<div className="grid grid-cols-1 @min-[933px]:grid-cols-3 p-4 gap-4 min-w-0">
					<div className="h-[540px] flex min-w-0">
						<div className="flex flex-col flex-1 min-w-0">
							<Text as="div" type="Body/L/Bold">
								Relevant variables
							</Text>
							<DonutChartSvg
								onSectorClick={(_sector, index) => syncScrollableAreaByIndex(index)}
								onSectorMouseEnter={(_sector, index) => setHoveringIndicatorIndex(index)}
								onSectorMouseLeave={() => setHoveringIndicatorIndex(null)}
								hoveringSectorIndex={hoveringIndicatorIndex}
								selectedSectorIndex={selectedIndicatorIndex}
								className="flex-1 min-w-0"
								innerText={
									selectedIndicatorIndex === null && hoveringIndicatorIndex === null
										? [{ text: "RELEVANCE" }]
										: [
												{
													text: `${
														performanceIndicators[(hoveringIndicatorIndex ?? selectedIndicatorIndex)!].relevance
													}%`,
													className: "font-semibold text-base",
												},
												{
													text: performanceIndicators[(hoveringIndicatorIndex ?? selectedIndicatorIndex)!].label,
												},
											]
								}
								sectors={performanceIndicators.map((indicator) => ({
									label: indicator.label,
									value: indicator.relevance,
									color: indicator.color,
								}))}
							/>
						</div>
						<div className={`hidden @min-[933px]:block h-full w-px bg-[var(--palette-N100)] -mt-4`} />
					</div>
					<div className="@min-[933px]:col-span-2 min-w-0">
						<Text as="div" type="Body/L/Bold" className="mb-4">
							Performance in similar periods
						</Text>
						<div className="grid grid-cols-4 gap-y-6 @min-[1189px]:grid-cols-5 @min-[1189px]:gap-y-0">
							<div className={`col-span-2 min-w-0 border-r border-[color:var(--palette-N100)] border-solid`}>
								<div className="flex justify-center">
									<div className="flex flex-col items-center">
										<Text as="p" type="Body/M/Book" className="font-semibold! uppercase w-fit text-center mb-2.5">
											RETURNS
										</Text>
										<div className={`h-px w-8 bg-[var(--palette-N100)]`} />
									</div>
								</div>
								<div className="flex h-[460px] min-w-0">
									<BubbleChartSvg
										boundsMode="mixed"
										selectedBubbleIndex={selectedIndicatorIndex}
										hoveringBubbleIndex={hoveringIndicatorIndex}
										lowerbound={ScatterPlotLimits[plotKey].RETURNS_LIMITS.min}
										upperbound={ScatterPlotLimits[plotKey].RETURNS_LIMITS.max}
										onCircleClick={(_, i) => syncScrollableAreaByIndex(i)}
										onCircleMouseEnter={(_, i) => setHoveringIndicatorIndex(i)}
										onCircleMouseLeave={() => setHoveringIndicatorIndex(null)}
										bubbles={performanceIndicators.map((indicator) => ({
											color: macroDynamicsColorGenerator(indicator.returnTimeSeries, {
												max: returnsLimits[returnsLimits.length - 1],
												min: returnsLimits[0],
												intermediateMax: returnsLimits[returnsLimits.length - 2],
												intermediateMin: returnsLimits[1],
											}),
											label: `${formatNumber(indicator.returnTimeSeries)}%`,
											size: indicator.relevance,
											value: indicator.returnTimeSeries,
											hide: indicator.label === "Quantitative Market Sentiment",
										}))}
										className="flex-1 h-full min-w-0"
										bottomLabel="Time Series"
										labels={returnsLimits.map((range) => ({
											text: `${range}%`,
											threshold: range,
										}))}
									/>
									<BubbleChartSvg
										boundsMode="bounded"
										selectedBubbleIndex={selectedIndicatorIndex}
										hoveringBubbleIndex={hoveringIndicatorIndex}
										onCircleClick={(_, i) => syncScrollableAreaByIndex(i)}
										onCircleMouseEnter={(_, i) => setHoveringIndicatorIndex(i)}
										onCircleMouseLeave={() => setHoveringIndicatorIndex(null)}
										bubbles={performanceIndicators.map((indicator) => ({
											color: crossSectionalColorGenerator(indicator.returnVsPeers),
											label: ordinal(indicator.returnVsPeers),
											size: indicator.relevance,
											value: indicator.returnVsPeers,
										}))}
										bottomLabel="Cross sectional"
										flipY
										className="flex-1 h-full min-w-0"
										labels={threshold.map((x) => ({
											text: formatNumber(x, 0),
											threshold: x,
										}))}
									/>
								</div>
							</div>
							<div
								className={`col-span-2 min-w-0 @min-[1189px]:border-r border-[color:var(--palette-N100)] border-solid`}
							>
								<div className="flex justify-center">
									<div className="flex flex-col items-center">
										<Text as="p" type="Body/M/Book" className="font-semibold! uppercase w-fit text-center mb-2.5">
											VOLATILITY
										</Text>
										<div className={`h-px w-8 bg-[var(--palette-N100)]`} />
									</div>
								</div>
								<div className="flex h-[460px] min-w-0">
									{/* // volatility should be inverted as less volatility is more is positive */}
									<BubbleChartSvg
										flipY
										boundsMode="unbounded"
										selectedBubbleIndex={selectedIndicatorIndex}
										hoveringBubbleIndex={hoveringIndicatorIndex}
										onCircleClick={(_, i) => syncScrollableAreaByIndex(i)}
										onCircleMouseEnter={(_, i) => setHoveringIndicatorIndex(i)}
										onCircleMouseLeave={() => setHoveringIndicatorIndex(null)}
										bubbles={performanceIndicators.map((indicator) => ({
											color: macroDynamicsColorGenerator(indicator.volatilityTimeSeries, {
												max: volatilityLimits[returnsLimits.length - 1],
												min: volatilityLimits[0],
												intermediateMax: volatilityLimits[returnsLimits.length - 2],
												intermediateMin: volatilityLimits[1],
												invert: true,
											}),
											label: `${formatNumber(indicator.volatilityTimeSeries)}%`,
											size: indicator.relevance,
											value: indicator.volatilityTimeSeries,
											hide: indicator.label === "Quantitative Market Sentiment",
										}))}
										bottomLabel="Time Series"
										className="flex-1 h-full min-w-0"
										labels={volatilityLimits.map((range) => ({
											text: `${range}%`,
											threshold: range,
										}))}
									/>
									<BubbleChartSvg
										boundsMode="bounded"
										selectedBubbleIndex={selectedIndicatorIndex}
										hoveringBubbleIndex={hoveringIndicatorIndex}
										onCircleClick={(_, i) => syncScrollableAreaByIndex(i)}
										onCircleMouseEnter={(_, i) => setHoveringIndicatorIndex(i)}
										onCircleMouseLeave={() => setHoveringIndicatorIndex(null)}
										bubbles={performanceIndicators.map((indicator) => ({
											color: crossSectionalColorGenerator(indicator.volatilityVsPeers),
											label: ordinal(indicator.volatilityVsPeers),
											size: indicator.relevance,
											value: indicator.volatilityVsPeers,
										}))}
										bottomLabel="Cross sectional"
										flipY
										className="flex-1 h-full min-w-0"
										labels={threshold.map((x) => ({
											text: formatNumber(x, 0),
											threshold: x,
										}))}
									/>
								</div>
							</div>
							<div className="col-span-1 min-w-0">
								<div className="flex justify-center">
									<div className="flex flex-col items-center">
										<Text as="p" type="Body/M/Book" className="font-semibold! uppercase w-fit text-center mb-2.5">
											DIVERSIFICATION POWER
										</Text>
										<div className={`h-px w-8 bg-[var(--palette-N100)]`} />
									</div>
								</div>
								<div className="flex h-[460px] min-w-0">
									<BubbleChartSvg
										boundsMode="bounded"
										selectedBubbleIndex={selectedIndicatorIndex}
										hoveringBubbleIndex={hoveringIndicatorIndex}
										onCircleClick={(_, i) => syncScrollableAreaByIndex(i)}
										onCircleMouseEnter={(_, i) => setHoveringIndicatorIndex(i)}
										onCircleMouseLeave={() => setHoveringIndicatorIndex(null)}
										bubbles={performanceIndicators.map((indicator) => ({
											color: crossSectionalColorGenerator(indicator.diversificationVsPeers),
											label: ordinal(indicator.diversificationVsPeers),
											size: indicator.relevance,
											value: indicator.diversificationVsPeers,
										}))}
										bottomLabel="Cross sectional"
										flipY
										className="flex-1 h-full min-w-0"
										labels={threshold.map((x) => ({
											text: formatNumber(x, 0),
											threshold: x,
										}))}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Card>
	);
}

function SummaryCard({ summary, VaR }: { summary: AssetClassSummary; VaR: Array<LabelValueV2> }) {
	const { formatNumber } = useLocaleFormatters();
	return (
		<Card>
			<div className="@container" data-qualifier={qualifier.outlook.focus.summary}>
				{/* Below the breakpoint each metric is a full-width row that borrows the outer grid's columns
					(grid-cols-subgrid), so YTD/1M/3M/6M/1Y line up across metrics. Above it the three metrics
					sit side by side and each lays out its own cells as badge | highlight-over-periods. */}
				<div className="grid grid-cols-[max-content_auto_repeat(5,auto)] gap-x-4 gap-y-2 @min-[1189px]:grid-cols-[1fr_1fr_auto]">
					<ForEach
						collection={
							[
								[
									"RETURNS",
									["Expected", summary.expectedReturn],
									[
										["YTD", summary.return?.returnYTD],
										["1M", summary.return?.return1M],
										["3M", summary.return?.return3M],
										["6M", summary.return?.return6M],
										["1Y", summary.return?.return1Y],
									],
								],
								[
									"VOLATILITY",
									["Ann. Expected", summary.expectedVolatility],
									[
										["YTD", summary.volatility?.volatilityYTD],
										["1M", summary.volatility?.volatility1M],
										["3M", summary.volatility?.volatility3M],
										["6M", summary.volatility?.volatility6M],
										["1Y", summary.volatility?.volatility1Y],
									],
								],
								["VaR", ["Expected 95% 1M", VaR[0]?.value?.value], [["95% 3Y", VaR[1]?.value?.value]]],
							] as const
						}
					>
						{([title, [highlightLabel, highlightValue], subItems]) => (
							<div className="col-span-7 grid grid-cols-subgrid gap-x-4 min-w-0 @min-[1189px]:col-span-1 @min-[1189px]:grid-cols-[max-content_repeat(5,auto)]">
								<div
									className={`px-2 py-1.5 bg-[color:var(--palette-N50)] rounded-sm flex justify-center items-center @min-[1189px]:row-span-2`}
								>
									<Text as="div" type="Body/M/Bold" color={themeCSSVars.palette_N800}>
										{title}
									</Text>
								</div>
								<div className="relative z-0 self-center @min-[1189px]:col-span-5">
									<Text type="Body/S/Light-UPPERCASE" color={themeCSSVars.palette_N400}>
										{highlightLabel}
									</Text>
									&nbsp;
									<Text type="Body/L/Bold">{formatNumber(highlightValue)}%</Text>
								</div>
								<ForEach collection={subItems}>
									{([label, value]) => (
										<div className="self-center">
											<Text type="Body/S/Light-UPPERCASE" color={themeCSSVars.palette_N400}>
												{label}
											</Text>
											&nbsp;
											<Text type="Body/M/Bold">{formatNumber(value)}%</Text>
										</div>
									)}
								</ForEach>
							</div>
						)}
					</ForEach>
				</div>
			</div>
		</Card>
	);
}

const colors = lazy(() => [
	getThemeCssVars(themeCSSVars.palette_S500),
	getThemeCssVars(themeCSSVars.palette_W500),
	getThemeCssVars(themeCSSVars.palette_R500),
	getThemeCssVars(themeCSSVars.palette_B500),
	getThemeCssVars(themeCSSVars.palette_A500),
	getThemeCssVars(themeCSSVars.palette_D500),
	getThemeCssVars(themeCSSVars.palette_V500),
	getThemeCssVars(themeCSSVars.palette_P500),
	getThemeCssVars(themeCSSVars.palette_S700),
	getThemeCssVars(themeCSSVars.palette_V700),
]);

function CurrentPositioningCard({
	summary,
	assetClass,
	geography,
	sector,
	isSummaryLoading,
}: {
	summary: AssetClassSummary;
	assetClass?: string;
	geography?: string;
	sector?: string;
	isSummaryLoading: boolean;
}) {
	const [cacheMarketOutlookSeries, setCacheMarketOutlookSeries] = useState<MarketOutlookTimeSeriesDTO | undefined>(
		undefined,
	);

	const timeSeriesQuery = outlookRepository.positioningContinuousTimeSeries.useQuery({
		assetClass: assetClass as any,
		geography: geography as any,
		sector: sector as any,
		fromDate: useMemo(() => {
			const fromDate = new Date();
			fromDate.setFullYear(fromDate.getFullYear() - 1);
			fromDate.setHours(0, 0, 0, 0);
			return fromDate.toISOString().split("T")[0];
		}, []),
	});
	useQueryReactor(timeSeriesQuery, {
		onSuccess: setCacheMarketOutlookSeries,
	});
	const { isFetching } = timeSeriesQuery;
	const timeSeries = useMemo(
		() =>
			cacheMarketOutlookSeries?.values
				?.filter(([_x, y]) => y ?? false)
				?.map(
					([dateSeconds, y]) =>
						[dateSeconds * 1000, y, cacheMarketOutlookSeries?.inverted ?? false] satisfies [number, number, boolean],
				),
		[cacheMarketOutlookSeries],
	);

	const assetClassLabel = useMemo(
		() => cacheMarketOutlookSeries?.type ?? assetClass ?? "-",
		[assetClass, cacheMarketOutlookSeries?.type],
	);

	return (
		<Card title="Current positioning">
			<div className="relative z-0" data-qualifier={qualifier.outlook.focus.positioning}>
				<LocalOverlay
					show={isSummaryLoading}
					className="z-10"
					style={{
						background: "white",
					}}
				>
					<CircularProgressBar value="indeterminate" />
				</LocalOverlay>
				<div className="mb-2 flex items-center">
					{summary.exposureLabel?.[0]?.value && (
						<PositioningBadge
							positioning={
								positioningValueTypeMap[summary.exposureLabel[0].value as keyof typeof positioningValueTypeMap]
							}
							className="flex-1 mr-4"
						/>
					)}

					<TimeSeriesModal
						assetClass={assetClassLabel}
						currentRegime={
							summary.exposureLabel?.[0]?.value
								? {
										label:
											positioningPresets[
												positioningValueTypeMap[summary.exposureLabel[0].value as keyof typeof positioningValueTypeMap]
											].label,
										value: summary.exposureLabel[0].value,
									}
								: {
										label: "-",
									}
						}
						previousRegime={
							summary.prevValue?.value
								? {
										label:
											positioningPresets[
												positioningValueTypeMap[summary.prevValue.value as keyof typeof positioningValueTypeMap]
											].label,
										value: summary.prevValue.value,
									}
								: {
										label: "-",
									}
						}
						isLoading={isFetching}
						series={
							timeSeries
								? {
										[assetClassLabel]: timeSeries,
									}
								: undefined
						}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<Text as="div" type="Body/L/Bold" className="mb-2">
							Drivers
						</Text>
						<div className="flex flex-col items-start gap-2">
							<ForEach
								collection={
									summary.driverLabel?.flatMap((x) => {
										const feel = feelsConverter(x);
										return feel ? [feel] : [];
									}) ?? []
								}
							>
								{(item) => (
									<SentimentBadge sentiment={item.sentiment} indicator={item.indicator}>
										{item.label}
									</SentimentBadge>
								)}
							</ForEach>
						</div>
					</div>
					<div>
						<Text as="div" type="Body/L/Bold" className="mb-2">
							Regime
						</Text>
						<ForEach
							collection={
								summary.actualRegime?.flatMap((x) => {
									const feel = feelsConverter({ ...x, type: "RISK" });
									return feel ? [feel] : [];
								}) ?? []
							}
						>
							{(item) => (
								<SentimentBadge sentiment={item.sentiment} indicator={item.indicator}>
									{item.label}
								</SentimentBadge>
							)}
						</ForEach>
					</div>
				</div>
			</div>
		</Card>
	);
}
